import Vue from 'vue';
import { getInitialDeckung, getInitialFahrzeugData, getInitialFirma, getInitialImmobilie, getInitialMitarbeiterData, getInitialPerson, getInitialPraemie, getInitialState, getInitialVersicherung, getInitialVorversicherung, getInitialWohnung } from './gewerbeObjects';
import { preciseRound } from '../helper/praemienHelper';
import httpService from "@/helper/httpService";
import {getDateWithTimeString} from "@/helper/dateService";

const PRAEMIEN_KEYS = ['base', 'z1', 'z2', 'z4', 'z12'];

function getFahrzeugeByFlotte(fahrzeuge) {
  return Object.values(fahrzeuge).reduce((result, fahrzeug) => {
    result[fahrzeug.flotte] += Number(fahrzeug.anzahl) || 0;
    return result;
  }, { A: 0, B: 0, C: 0 });
}

function isFreeDeckung(state, getters, deckung) {
  const ersterGF = getters.ersterGF;
  if (!ersterGF || deckung.ref !== ersterGF.key) return false;
  if (deckung.produkt === 'SPEZ-PRIV') return true;
  return deckung.produkt === 'SPEZ-PRIVSSR'
    && getters.gewerbeProductSelected('SPEZ-SSRG', 'vn1');
}

function isZeroPraemieAllowed(state, getters, deckung) {
  if (isFreeDeckung(state, getters, deckung)) return true;
  if (!['VERKA', 'VERKB', 'VERKC'].includes(deckung.produkt)) return false;

  const fahrzeuge = getFahrzeugeByFlotte(state.fahrzeuge);
  const flotte = deckung.produkt.substr(4);
  return fahrzeuge[flotte] === 0 || (flotte === 'C' && fahrzeuge.A + fahrzeuge.B > 4);
}

function calculateDeckungPraemie(state, getters, deckung) {
  if (isFreeDeckung(state, getters, deckung)) return getInitialPraemie();

  const immobilien = deckung.immoRef
    ? state.immobilien.filter(immobilie => immobilie.key === deckung.immoRef)
    : deckung.produkt === 'MIET' ? state.immobilien : [];
  const wohnungen = deckung.wohnRef
    ? state.wohnungen.filter(wohnung => wohnung.key === deckung.wohnRef)
    : [];
  const produkt = deckung.produkt === 'MIETZ' ? 'MIET' : deckung.produkt;
  const params = [state.mitarbeiter, state.fahrzeuge, immobilien, wohnungen, state.vertrag.laufzeit];
  const praemie = {
    base: getters.gewerbePrice(deckung.sb, produkt, ...(params.concat([null, true])))
  };
  ['1', '2', '4', '12'].forEach(zahlweise => {
    praemie[`z${zahlweise}`] = getters.gewerbePrice(deckung.sb, produkt, ...(params.concat([zahlweise])));
  });
  return praemie;
}

function getInvalidPraemieKey(state, getters, deckung) {
  if (['MIET', 'MIETZ'].includes(deckung.produkt)
      && (!deckung.immoRef || !state.immobilien.some(immobilie => immobilie.key === deckung.immoRef))) {
    return 'immoRef';
  }
  if (deckung.produkt === 'SPEZ-PRIVVM'
      && (!deckung.wohnRef || !state.wohnungen.some(wohnung => wohnung.key === deckung.wohnRef))) {
    return 'wohnRef';
  }

  const zeroAllowed = isZeroPraemieAllowed(state, getters, deckung);
  return PRAEMIEN_KEYS.find(key => {
    const value = deckung.praemie && deckung.praemie[key];
    return typeof value !== 'number' || !Number.isFinite(value) || (value === 0 && !zeroAllowed);
  });
}

function createPraemienValidationError(deckung) {
  const error = new Error(`Die Prämie für die Deckung ${deckung.produkt} (${deckung.ref}) konnte nicht berechnet werden.`);
  error.isPraemienValidationError = true;
  error.deckung = deckung;
  return error;
}

const state = getInitialState();

const getters = {
  personPath: (state) => (key) => {
    let index = state.personen.findIndex(p => p.key === key);
    return `gewerbe.personen.${index}`;
  },
  vorversicherungPath: (state) => (ref) => {
    let index = state.vorversicherungen.findIndex(v => v.ref === ref);
    return `gewerbe.vorversicherungen.${index}`;
  },
  wohnungPath: (state) => (key, parent) => {
    let index = state[parent].findIndex(zw => zw.key === key);
    return `gewerbe.${parent}.${index}`;
  },
  immobiliePath: (state) => (key) => {
    let index = state.immobilien.findIndex(i => i.key === key);
    return `gewerbe.immobilien.${index}`;
  },
  gewerbeDeckung: (state) => (person, produkt, immoRef, wohnRef) => {
    let deckungen = state.deckungen.filter(d => d.ref === person && d.produkt === produkt);
    if (immoRef) deckungen = deckungen.filter(d => d.immoRef === immoRef);
    if (wohnRef) deckungen = deckungen.filter(d => d.wohnRef === wohnRef);
    return deckungen[0];
  },
  mvUnternehmen: (state) => {
    return state.personen.filter(p => /^mvu[0-9]*$/.test(p.key));
  },
  filialen: (state) => {
    return state.personen.filter(p => /^fil[0-9]*$/.test(p.key));
  },
  geschaeftsfuehrer: (state) => {
    return state.personen.filter(p => /^gf[0-9]*$/.test(p.key));
  },
  ersterGF: (state) => {
    let gfeins = null;
    state.personen.filter(p => /^gf[0-9]*$/.test(p.key)).forEach(gf => {
      if (!gfeins) gfeins = gf;
      if (+(gfeins.key.substring(2)) > +(gf.key.substring(2))) gfeins = gf;
    });
    return gfeins;
  },
  mvPersonen: (state) => {
    return state.personen.filter(p => /^mvp[0-9]*$/.test(p.key));
  },
  versicherungsnehmer: (state) => {
    return state.personen.find(p => p.key === 'vn1');
  },
  anzahlMitarbeiter: (state) => {
    const anzahl = Object.keys(state.mitarbeiter).reduce((sum, m) => {
      const anzahl = +state.mitarbeiter[m].anzahl;
      return isNaN(anzahl) ? sum : sum + anzahl * state.mitarbeiter[m].faktor;
    }, 0);
    return Math.floor(anzahl + 0.49);
  },
  anzahlFahrzeuge: (state) => {
    return Object.keys(state.fahrzeuge).reduce((sum, f) => {
      const anzahl = +state.fahrzeuge[f].anzahl;
      return isNaN(anzahl) ? sum : sum + anzahl;
    }, 0);
  },
  relevanteFahrzeuge: (state) => {
      let a = 0, b = 0, c = 0;
      Object.values(state.fahrzeuge).forEach(f => {
        switch(f.flotte){
          case 'A':
            a += +f.anzahl;
            break;
          case 'B':
            b += +f.anzahl;
            break;
          case 'C':
            c += +f.anzahl;
            break;
        }
      });
      return ( a + b > 4) ? a+b : a+b+c;
  },
  gewerbeProductSelected: (state) => (produkt, personKey) => {
    return !!state.deckungen.find(deckung => deckung.produkt === produkt && deckung.ref === personKey);
  },
  kompexHauptproduktPraemie: (state) => (zahlweise) => {
    return state.deckungen.reduce((sum, d) => {
      return d.ref === 'vn1' && ['SPEZ-PBV', 'SPEZ-PBV+MIET', 'SPEZ-PRIVMINUSGF'].includes(d.produkt) ?
        sum + d.praemie[zahlweise] : sum;
    }, 0);
  },
  /**
 * Sum of Deckungen
 * @param {string} deckungkey 'MIET', 'MIETZ', ...
 * @param {string} [zahlweisekey=state.vertrag.zahlweise] - optional valid values 'base', 'z1', 'z2' 
 * @returns number
 */
  gewerbeDeckungPrice: state => (deckungkey, zahlweisekey) => {
    const zahlweise = zahlweisekey ? zahlweisekey : "z" + state.vertrag.zahlweise;
    const deckungen = state.deckungen.filter( d => d.produkt === deckungkey && d.ref === 'vn1');
    return deckungen.length ? deckungen.reduce((sum, deckung) => 
      sum + ((deckung.praemie) ? deckung.praemie[zahlweise] : 0)
    , 0 ) : 0;
  },
  /**
  * @returns kontoinhaber if it is not the same person as versicherungsnehmer, else undefined
  * */
  kontoinhaber: (state) => {
    return state.personen.find(p => /^kto[0-9]*$/.test(p.key));
  }
};

const mutations = {
  /**
   * */
  setGewerbeIdentity(state, user) {
    state.selectedIdentity = user;
  },
  /**
   * @param state
   * */
  addMvUnternehmen: (state) => {
    let mvu = getInitialFirma(`mvu${state.ui.sequentialNumber++}`);
    state.personen.push(mvu);
  },
  /**
   * @param state
   * */
  gewerbeSetFilialenCount: (state) => {
    const filialen = state.personen.filter(p => (/^fil[0-9]*$/.test(p.key)));
    state.personen = state.personen.filter(p => !(/^fil[0-9]*$/.test(p.key)));
    if (state.ui.hasFilialen) {
      const vn = state.personen.find(p => p.key === 'vn1');
      for (let i=0; i < vn.anzahlFilialen; i++) {
        if (i < filialen.length) {
          state.personen.push(filialen[i]);
        } else {
          state.personen.push(getInitialFirma(`fil${state.ui.sequentialNumber++}`, 'vn1'));
        }
      }
    }
  },
  
  /**
   * @param state
   * */
  telefonCleanup: (state) => {
    state.personen.forEach(p => {
      if(p.telefon && p.telefon === "+49"){
        p.telefon = "";
      }
    });
  },
  /**
   * @param state
   * */
  addGeschaeftsfuehrer: (state) => {
    let gf = getInitialPerson(`gf${state.ui.sequentialNumber++}`);
    state.personen.push(gf);
    const gfCount = state.personen.filter(p => (/^gf[0-9]*$/.test(p.key))).length;
    if (state.ui.isKompex && +state.mitarbeiter.inhaber.anzahl < gfCount) state.mitarbeiter.inhaber.anzahl = '' + gfCount;
  },
  gewerbeUpdateGfCount: (state) => {
    const gfCount = state.personen.filter(p => (/^gf[0-9]*$/.test(p.key))).length;
    if (+state.mitarbeiter.inhaber.anzahl < gfCount) state.mitarbeiter.inhaber.anzahl = '' + gfCount;
  },
  /**
   * @param state
   * @param {object} payload - {
   *    gf: string - key of associated Geschaeftsfuehrer
   *  }
   * */
  addMvPerson: (state, { gf }) => {
    if (state.personen.find(p => /^mvp[0-9]*$/.test(p.key) && p.ref === gf) === undefined) {
      const mvp = getInitialPerson(`mvp${state.ui.sequentialNumber++}`, gf);
      state.personen.push(mvp);
    }
    if (state.ui.hasMvPerson.indexOf(gf) < 0) state.ui.hasMvPerson.push(gf);
  },
  /**
   * @param state
   * @param {object} payload - { key: string }
   * */
  removePerson: (state, { key }) => {
    let index = state.personen.findIndex(p => p.key === key);
    if (index > -1) state.personen.splice(index, 1);
    // delete possible Deckungen
    state.deckungen = state.deckungen.filter(d => d.ref !== key);
    // delete possible Wohnungen
    state.wohnungen = state.wohnungen.filter(w => w.ref !== key);
  },
  /**
   * @param state
   * */
  addImmobilie: (state, payload = {}) => {
    state.ui.showKompexOhneImmobilien = false;
    let immobilie = Object.assign( getInitialImmobilie(`immo${state.ui.sequentialNumber++}`), payload);
    state.immobilien.push(immobilie);
  },
  /**
   * @param state
   * @param {object} payload - {
   *    person: string  - person key
   *  }
   * */
  gewerbeAddWohnung: (state, {person}) => {
    const wohnung = getInitialWohnung(`wohn${state.ui.sequentialNumber++}`, person);
    state.wohnungen.push(wohnung);
  },
  /**
   * @param state
   * @param {object} payload - { sb }
   * */
  gewerbeSetSB: (state, { sb }) => {
    state.vertrag.sb = sb;
  },
  /**
   * @param state
   * @param {object} payload - { deckungen }
   * */
  gewerbeSetDeckungen: (state, { deckungen }) => {
    state.deckungen = deckungen;
  },
  /**
   * @param state
   * @param {object} payload - { immobilien }
   * */
  gewerbeSetImmobilien: (state, { immobilien }) => {
    state.immobilien = immobilien;
  },
  /**
   * @param state
   * @param {object} payload - { wohnungen }
   * */
  gewerbeSetWohnungen: (state, { wohnungen }) => {
    state.wohnungen = wohnungen;
  },
  /**
   * @param state
   * @param {object} payload - {
   *    ref: string - key of associated Person
   *  }
   * */
  gewerbeAddVorversicherung: (state, { ref }) => {
    const vorversicherungen = state.vorversicherungen.find(v => v.ref === ref);
    vorversicherungen.versicherungen.push(getInitialVersicherung());
  },
  /**
   * @param state
   * @param {object} payload - {
   *    ref: string - key of associated Person
   *  }
   * */
  gewerbeRemoveVorversicherung: (state, { ref }) => {
    const vorversicherungen = state.vorversicherungen.find(v => v.ref === ref);
    vorversicherungen.versicherungen.pop();
  },
  gewerbeStep4Radios: (state, { ref, type, value }) => {
    state.ui[type][ref] = value;
  },
  gewerbeUpdateTotalPraemie: (state) => {
    state.vertrag.praemie = getInitialPraemie();
    state.deckungen.forEach(deckung => {
      Object.keys(deckung.praemie).forEach(key => {
        state.vertrag.praemie[key] += deckung.praemie[key];
      });
    });
    Object.keys(state.vertrag.praemie).forEach(key => state.vertrag.praemie[key] = preciseRound(state.vertrag.praemie[key]));
  },
  gewerbeDeleteMatrixSelection: (state) => {
    state.ui.matrixSelection = {
      sb: {
        gewerbe: null,
         privat: null,
      },
      produkte: []
    };
    state.ui.excludeGF = false;
  },
  gewerbeCleanupBaseDataMVU: (state) => {
    if (!state.ui.hasFilialen) {
      let vn1 = state.personen.find(p => p.key === 'vn1');
      vn1.anzahlFilialen = 0;
    }
    if (!state.ui.hasMvUnternehmen) {
      state.personen = state.personen.filter(p => !(/^mvu[0-9]*$/.test(p.key)));
    }
  },
  gewerbeCleanupBaseData: (state) => {
    // step 1
    if (state.ui.isKompex) {
      state.ui.produkte = [];
      state.ui.hideNotKompex = true;
    }
    if (!state.ui.isKompex && state.ui.produkte.indexOf('FIRM') < 0) {
      state.mitarbeiter = getInitialMitarbeiterData();
    }
    if (state.ui.isKompex || state.ui.produkte.indexOf('VERK') < 0) {
      state.fahrzeuge = getInitialFahrzeugData();
    }
    if (state.ui.isKompex) {
      if (state.deckungen.find(d => d.produkt === 'MIETZ')) {
        state.immobilien = state.immobilien.filter(i => i.art === 'VERMIET');
      } else {
        state.immobilien = [];
      }
    } else if (state.ui.produkte.indexOf('MIET') < 0) {
      state.immobilien = [];
    }
    // step 2
    if (!state.ui.isKompex) {
      state.personen = state.personen.filter(p => !(/^gf[0-9]*$/.test(p.key)));
      state.wohnungen = [];
    }
    // step 3
    state.ui.hasMvPerson = state.ui.hasMvPerson.filter(v => !!state.personen.find(p => p.key === v));
  },
  gewerbeAddKontoinhaber: (state) => {
    const inhaber = getInitialPerson(`kto${state.ui.sequentialNumber++}`);
    state.personen.push(inhaber);
    state.konto.ref = inhaber.key;
  },
  gewerbeCleanupPersonData: (state) => {
    // cleanup step 3
    if (state.vertrag.rechnungszahler === "J" || state.vertrag.rechnungszahler === "M") {
      state.konto = {
        iban: null,
        bic: null,
        ref: "vn1"
      };
    }
    if (state.vertrag.rechnungszahler === "J" || state.konto.ref === "vn1") {
      state.personen = state.personen.filter(p => !(/^kto[0-9]*$/.test(p.key)));
    }
    if (!state.ui.hasMvUnternehmen) state.personen = state.personen.filter(p => !(/^mvu[0-9]*$/.test(p.key)));
    if (!state.ui.isKompex || state.ui.excludeGF) {
      state.personen = state.personen.filter(p => !(/^gf[0-9]*$/.test(p.key)));
      state.personen = state.personen.filter(p => !(/^mvp[0-9]*$/.test(p.key)));
    } else {
      const mvps = state.personen.filter(p => (/^mvp[0-9]*$/.test(p.key)));
      mvps.forEach(mvp => {
        const gf = state.personen.find(p => p.key === mvp.ref);
        if (gf === undefined || state.ui.hasMvPerson.indexOf(gf.key) < 0) {
          const index = state.personen.findIndex(p => p.key === mvp.key);
          if (index > -1) state.personen.splice(index, 1);
        }
      });
    }
    // prepare for step 4
    Object.keys(state.ui.hasVorversicherung).forEach(key => {
      if (!state.personen.find(p => p.key === key)) delete state.ui.hasVorversicherung[key];
    });
    Object.keys(state.ui.hasStreitigkeiten).forEach(key => {
      if (!state.personen.find(p => p.key === key)) delete state.ui.hasVorversicherung[key];
    });
    Object.keys(state.ui.hasAnwaltsbesuche).forEach(key => {
      if (!state.personen.find(p => p.key === key)) delete state.ui.hasVorversicherung[key];
    });
    state.vorversicherungen = state.vorversicherungen.filter(v => !!state.personen.find(p => p.key === v.ref));
    state.personen.filter(p => !(/^kto[0-9]*$/.test(p.key)) && !(/^fil[0-9]*$/.test(p.key))).forEach(p => {
      if (!state.vorversicherungen.find(v => v.ref === p.key)) {
        state.vorversicherungen.push(getInitialVorversicherung(p.key));
        Vue.set(state.ui.hasVorversicherung, p.key, null);
        Vue.set(state.ui.hasStreitigkeiten, p.key, null);
        Vue.set(state.ui.hasAnwaltsbesuche, p.key, null);
      }
    });
    // delete outdated Wohnungen
    state.wohnungen = state.wohnungen.filter(w => state.deckungen.find(d => d.wohnRef === w.key && d.produkt === 'SPEZ-PRIVVM'));
    // create Ferienwohnungen
    const gfKeys = state.personen.map(p => p.key)
      .filter(key => (/^gf[0-9]*$/.test(key)))
      .filter(key => state.deckungen.find(d => d.ref === key && d.produkt === 'SPEZ-PRIVZW'));
    state.ferienwohnungen = state.ferienwohnungen.filter(zw => gfKeys.includes(zw.ref));
    gfKeys.forEach(gf => {
      if (!state.ferienwohnungen.find(zw => zw.ref === gf)){
        let zwkey = `zw${state.ui.sequentialNumber++}`;
        state.ferienwohnungen.push(getInitialWohnung(zwkey, gf));
        state.deckungen.find(d => d.ref === gf && d.produkt === 'SPEZ-PRIVZW').wohnRef = zwkey;
      }
    });
  },
  gewerbeCleanupContractData: () => {
    state.vorversicherungen = state.vorversicherungen.map(v => {
      if (!state.ui.hasVorversicherung[v.ref]) v.versicherungen = [];
      if (!state.ui.hasStreitigkeiten[v.ref]) {
        v.anzahlrechtsstreitigkeiten = null;
        v.leistungshoeherechtsstreitigkeiten = null;
      }
      if (!state.ui.hasAnwaltsbesuche[v.ref]) {
        v.anzahlanwaltsbesuche = null;
        v.leistungshoeheanwaltsbesuche = null;
      }
      return v;
    });
  },
   /**
   * Display single ErrorMessage in a Snackbar
   * @param state
   * @param {string|object} msg - object { message:string, timeout:number, closebutton:boolean, isInfo:boolean}
   * missing or null keys in param object will be handeled by the component (set defaults)
   * */
  setSnackbarError: (state, msg) => {
    if (typeof(msg) === "string") {
      state.ui.errorSnackbar.errormessages=[msg];
      state.ui.errorSnackbar.timeout = null;
      state.ui.errorSnackbar.closebutton = null;
    } else {
      state.ui.errorSnackbar.errormessages=[msg.message];
      state.ui.errorSnackbar.timeout = (typeof(msg.timeout) === "number") ? msg.timeout : null;
      state.ui.errorSnackbar.closebutton = (typeof(msg.closebutton) === "boolean") ? msg.closebutton : null;
      state.ui.errorSnackbar.isInfo = (typeof(msg.isInfo) === "boolean") ? msg.isInfo : null;
    }
    state.ui.errorSnackbar.displayerrors = true;
  },
  gewerbeHideErrors: (state) => {
    state.ui.errorSnackbar.displayerrors = false;
    state.ui.errorSnackbar.timeout = null;
    state.ui.errorSnackbar.closebutton = null;
    state.ui.errorSnackbar.isInfo = null;
  },
  gewerbeSetAngebot: function (state, angebot) {
    state.vertrag.angebotsnummer = angebot.angebotsnummer;
    state.vertrag.submitTime = angebot.submitTime;
    state.vertrag.pdfpfad = angebot.pdfpfad;
    state.vertrag.guid = angebot.guid;
  },
  gewerbeResetAngebotData: function (state) {
    Object.assign(state, getInitialState());
  },
  gewerbeLoadAngebot(state, angebot) {
    Object.assign(state, getInitialState(), angebot);
    state.vertrag.aula = "N";
    state.personen.forEach(p => {
      if (!p.telefon) p.telefon = '+49';
    });
  },
};

const actions = {
  /**
   * Adds a new Deckung for a KOMPEX Zusatzbaustein and updates the total praemie.
   * Valid product keys (see parameter 'produkt') are (see also config/gewerbeZusatzbausteine.js):
   * gewerbe: SPEZ-T, SPEZ-SSR, MIETZ
   * privat: SPEZ-PRIV, SPEZ-PRIVCYBER, SPEZ-PRIVPRESTIGE, SPEZ-PRIVSSR, SPEZ-PRIVZW, SPEZ-PRIVVM
   * @param context
   * @param {object} payload - {
   *     person: string - person key
   *     sb: object { gewerbe: string, privat: string} - Selbstbeteiligung gewerblich und privat
   *     produkt: string - product key,
   *     wohnung?: string - wohnung key for SPEZ-PRIVVM
   *     immobilie?: string - immobilie key for MIETZ
   *     noUpdate?: boolean - prevents update of total praemie
   *     isFree?: boolean - price of the new Deckung is set to 0
   *  }
   * */
  gewerbeAddZusatzDeckung: ({state, getters, commit}, {person, sb, produkt, wohnung, immobilie, noUpdate, isFree}) => {
    let deckungen = state.deckungen.slice();
    const wohnungen = produkt === 'SPEZ-PRIVVM' && wohnung ? state.wohnungen.filter(w => w.key === wohnung) : [];
    const immobilien = produkt === 'MIETZ' && immobilie ? state.immobilien.filter(i => i.key === immobilie) : [];
    const priceParams = [state.mitarbeiter, null, immobilien, wohnungen, state.vertrag.laufzeit];
    const zahlweisen = ['1', '2', '4', '12'];
    const deckung = getInitialDeckung(person);
    const isValidImmobilie = immobilien[0] && immobilien[0].value && parseInt(immobilien[0].value) > 99;
    deckung.sb = sb;
    deckung.produkt = produkt;
    deckung.wohnRef = produkt === 'SPEZ-PRIVVM' ?  wohnung || null : null;
    const pricekey = produkt === 'MIETZ' ? 'MIET' : produkt;
    deckung.immoRef = produkt === 'MIETZ' ? immobilie || null : null;
    if (isFree || ((produkt === 'MIETZ') && !isValidImmobilie )) {
      deckung.praemie = getInitialPraemie();
    } else {
      deckung.praemie = {
        base: getters.gewerbePrice(sb, pricekey, ...(priceParams.concat([null, true])))
      };
      zahlweisen.forEach(zahlweise =>
        deckung.praemie[`z${zahlweise}`] = getters.gewerbePrice(sb, pricekey, ...(priceParams.concat([zahlweise]))));
    }

    deckungen.push(deckung);
    commit('gewerbeSetDeckungen', { deckungen });
    if (!noUpdate && !isFree) commit('gewerbeUpdateTotalPraemie');
  },
  /**
   * Removes an existing Deckung for a KOMPEX Zusatzbaustein and updates the total praemie.
   * @param context
   * @param {object} payload - { person: string, produkt: string, immoRef?: string, wohnRef?: string }
   * */
  gewerbeRemoveZusatzDeckung: ({state, commit}, {person, produkt, immoRef, wohnRef}) => {
    let deckungen = state.deckungen.filter(d =>
      d.ref !== person || d.produkt !== produkt || (immoRef && d.immoRef !== immoRef) || (wohnRef && d.wohnRef !== wohnRef));
    commit('gewerbeSetDeckungen', { deckungen });
    commit('gewerbeUpdateTotalPraemie');
  },
  /**
   * Sets the Deckungen for the selected 'main product' so to speak. That includes the following steps:
   * 1. Deleting the Deckungen for the previous matrix selection.
   * 2. If the currently selected product is KOMPEX
   *    - update the existing Deckungen for KOMPEX Zusatzbausteine (SB and price)
   *    - else delete all existing Deckungen for KOMPEX Zusatzbausteine
   * 3. Adding the Deckungen for the current matrix selection.
   * 4. Updating the total praemie.
   * It does NOT include updating the prices that are shown in the matrix!
   * Valid product keys (see parameter 'produkte') are:
   * - a combination of FIRM, VERKA, VERKB, VERKC, MIET
   * - SPEZ-PBV+MIET (with or without SPEZ-PRIVMINUSGF)
   * - SPEZ-PBV (with or without SPEZ-PRIVMINUSGF)
   * @param context
   * @param {object} payload - {
   *     sb: object { gewerbe: string, privat: string} - Selbstbeteiligung gewerblich und privat
   *     produkte: string[] - product keys e.g. ['FIRM', 'SPEZ-PBV']
   *  }
   * */
  gewerbeUpdateMatrixDeckung: ({state, getters, commit}, {sb, produkte}) => {
    let deckungen;

    const priceParams = [state.mitarbeiter, state.fahrzeuge, state.immobilien, state.wohnungen, state.vertrag.laufzeit];
    const zahlweisen = ['1', '2', '4', '12'];

    if (produkte.filter(p => /^SPEZ-PBV*/.test(p)).length === 0) {
      // no KOMPEX => remove all products and all additional Geschaeftsfuehrer
      deckungen = [];
      const gfs = state.personen.filter(p => (/^gf[0-9]*$/.test(p.key)));
      gfs.shift();
      gfs.forEach(gf => commit('removePerson', { key: gf.key }));
    } else {

      commit('gewerbeUpdateGfCount');

      // remove products from previous matrix selection
      const matrixProducts = ['FIRM', 'VERKA', 'VERKB', 'VERKC', 'MIET', 'SPEZ-PBV+MIET', 'SPEZ-PBV', 'SPEZ-PRIVMINUSGF'];
      const zusatzbausteine = state.deckungen.filter(deckung => matrixProducts.indexOf(deckung.produkt) < 0);
      // adjust sb and price for selected Zusatzbausteine
      deckungen = zusatzbausteine.map(deckung => {
        const params = priceParams.slice();
        params.splice(2, 1, deckung.immoRef ? state.immobilien.filter(i => i.key === deckung.immoRef) : []);
        params.splice(3, 1, deckung.wohnRef ? state.wohnungen.filter(w => w.key === deckung.wohnRef) : []);
        const adjustedDeckung = getInitialDeckung(deckung.ref);
        adjustedDeckung.sb = deckung.produkt === 'SPEZ-PRIVCYBER' || deckung.produkt === 'SPEZ-PRIVVM' ? deckung.sb : {
          gewerbe: sb.gewerbe,
          privat: sb.privat
        };
        adjustedDeckung.produkt = deckung.produkt;
        adjustedDeckung.wohnRef = deckung.wohnRef;
        adjustedDeckung.immoRef = deckung.immoRef;
        if (isFreeDeckung(state, getters, deckung)) {
          // Private coverage for the first GF can be included free of charge.
          adjustedDeckung.praemie = getInitialPraemie();
        } else {
          adjustedDeckung.praemie = {
            base: getters.gewerbePrice(adjustedDeckung.sb, deckung.produkt, ...(params.concat([null, true])))
          };
          zahlweisen.forEach(zahlweise =>
            adjustedDeckung.praemie[`z${zahlweise}`] =
              getters.gewerbePrice(adjustedDeckung.sb, deckung.produkt === 'MIETZ' ? 'MIET': deckung.produkt, ...(params.concat([zahlweise]))));
        }
        return adjustedDeckung;
      });
    }

    // create new Deckungen and calculate prices for everything but MIET
    const newDeckungen = produkte.filter(produkt => produkt !== 'MIET').map(produkt => {
      const deckung = getInitialDeckung('vn1');
      deckung.sb = sb;
      deckung.produkt = produkt;
      deckung.praemie = {
        base: getters.gewerbePrice(sb, produkt, ...(priceParams.concat([null, true])))
      };
      zahlweisen.forEach(zahlweise =>
        deckung.praemie[`z${zahlweise}`] = getters.gewerbePrice(sb, produkt, ...(priceParams.concat([zahlweise]))));
      return deckung;
    });

    // create one Deckung for each Immobilie
    let immobilienDeckungen = [];
    if (produkte.indexOf('MIET') > -1) {
      immobilienDeckungen = state.immobilien.map(immobilie => {
        const deckung = getInitialDeckung('vn1');
        deckung.sb = sb;
        deckung.produkt = 'MIET';
        deckung.immoRef = immobilie.key;
        const params = priceParams.slice();
        params.splice(2, 1, [immobilie]);
        deckung.praemie = {
          base: getters.gewerbePrice(sb, 'MIET', ...(params.concat([null, true])))
        };
        zahlweisen.forEach(zahlweise =>
          deckung.praemie[`z${zahlweise}`] = getters.gewerbePrice(sb, 'MIET', ...(params.concat([zahlweise]))));
        return deckung;
      });
    }
    commit('gewerbeSetDeckungen', { deckungen: deckungen.concat(newDeckungen).concat(immobilienDeckungen) });
    commit('gewerbeUpdateTotalPraemie');
    commit('gewerbeSetSB', { sb });
  },
  /**
   * @param context
   * */
  gewerbeResetMatrixSelection: ({commit}) => {
    commit('gewerbeSetDeckungen', { deckungen: [] });
    commit('gewerbeDeleteMatrixSelection');
  },
  /**
   * @param context
   * @param payload: object - {
   *    person: string - person key
   *    wohnung: string - wohnung key
   *    }
   * */
  gewerbeRemoveWohnung: ({state, commit, dispatch}, {person, wohnung}) => {
    const produkt = 'SPEZ-PRIVVM';
    dispatch('gewerbeRemoveZusatzDeckung', {person, produkt, wohnRef: wohnung});
    const wohnungen = state.wohnungen.filter(w => w.key !== wohnung);
    commit('gewerbeSetWohnungen', { wohnungen });
  },
  /**
   * @param context
   * @param {object} payload - { key: string }
   * */
  gewerbeRemoveImmobilie: ({state, commit}, {key}) => {
    const immobilien = state.immobilien.filter(i => i.key !== key);
    commit('gewerbeSetImmobilien', { immobilien });
    // delete possible Deckungen
    const deckungen = state.deckungen.filter(d => d.immoRef !== key);
    commit('gewerbeSetDeckungen', { deckungen });
    commit('gewerbeUpdateTotalPraemie');
  },
  /**
   * Updates Jahresbruttomiete of a specific Wohnung and the associated Deckung
   * @param context
   * @param payload: object -
   *    person: string - person key
   *    wohnung: string - wohnung key
   *    bruttojahresmiete: string
   *    sb: object - {
   *          gewerbe: string,
   *          privat: string
   *      }
   *    }
   * */
  gewerbeUpdateWohnung: ({state, commit, dispatch}, {person, wohnung, bruttojahresmiete, sb}) => {
    // update bruttojahresmiete of Wohnung object
    let index = state.wohnungen.findIndex(w => w.key === wohnung);
    const updatedWohnung = Object.assign({}, state.wohnungen[index]);
    updatedWohnung.bruttojahresmiete = bruttojahresmiete;
    const wohnungen = state.wohnungen.slice();
    wohnungen.splice(index, 1, updatedWohnung);
    commit('gewerbeSetWohnungen', { wohnungen });

    // remove possible previous Deckung
    const deckungen = state.deckungen.filter(d => d.wohnRef !== wohnung);
    commit('gewerbeSetDeckungen', { deckungen });

    // create new Deckung
    dispatch('gewerbeAddZusatzDeckung', { person, sb, produkt: 'SPEZ-PRIVVM', wohnung });
  },
  /**
   * Updates Sb of a all SPEZ-PRIVVM-Deckungen of a specific Person
   * @param context
   * @param payload: object - {
   *    person: string - person key
   *    sb: object - {
   *        gewerbe: string,
   *        privat: string
   *    }
   *  }
   * */
  gewerbeUpdateWohnungen: ({state, commit, dispatch}, {person, sb}) => {
    // remove possible previous Deckungen
    const deckungen = state.deckungen.filter(d => !(d.ref === person && d.produkt === 'SPEZ-PRIVVM'));
    commit('gewerbeSetDeckungen', { deckungen });

    // create new Deckungen
    state.wohnungen.filter(w => w.ref === person).forEach(w =>
        dispatch('gewerbeAddZusatzDeckung', { person, sb, produkt: 'SPEZ-PRIVVM', wohnung: w.key, noUpdate: true }
      ));

    commit('gewerbeUpdateTotalPraemie');
  },
  /**
   * Adds a Geschaeftsfuehrer and the associated Deckung
   * @param context
   * */
  gewerbeAddGeschaeftsfuehrer: ({state, commit, dispatch}) => {
    const person = `gf${state.ui.sequentialNumber}`;
    commit('addGeschaeftsfuehrer');
    dispatch('gewerbeAddZusatzDeckung', { person, sb: state.ui.matrixSelection.sb, produkt: 'SPEZ-PRIV'});
  },
  /**
   * Removes a Geschaeftsfuehrer and updates the prices
   * @param context
   * @param payload: object - {
   *    key: string - person key
   *  }
   * */
  gewerbeRemoveGeschaeftsfuehrer: ({state, commit, getters, dispatch}, {key}) => {

    // check if the GF to remove is the first GF ...
    const keys = getters.geschaeftsfuehrer.map(gf => +(gf.key.substring(2)));
    const key1 = Math.min(...keys);

    if (key === `gf${key1}`) {

      // ... in that case SPEZ-PRIV for the second GF is free
      const key2 = Math.min(...(keys.filter(k => k !== key1)));
      dispatch('gewerbeRemoveZusatzDeckung', { person: `gf${key2}`, produkt: 'SPEZ-PRIV' });
      dispatch('gewerbeAddZusatzDeckung', { person: `gf${key2}`, produkt: 'SPEZ-PRIV', sb: state.ui.matrixSelection.sb, isFree: true });

      // ... and possibly SPEZ-PRIVSSR as well
      if (getters.gewerbeProductSelected('SPEZ-SSRG', 'vn1')) {
        dispatch('gewerbeRemoveZusatzDeckung', { person: `gf${key2}`, produkt: 'SPEZ-PRIVSSR' });
        dispatch('gewerbeAddZusatzDeckung', { person: `gf${key2}`, produkt: 'SPEZ-PRIVSSR', sb: state.ui.matrixSelection.sb, isFree: true });
      }
    }

    commit('removePerson', { key });
    commit('gewerbeUpdateTotalPraemie');
  },
  /**
   * @param context
   * @param [payload]: object - {
   *    label: string ,
   *    limit: number|string
   *  }
   * */
  gewerbeShowDirektionsanfrage: ({commit}, payload) => {
    let message = 'Ein Online-Angebot kann für diese Produktauswahl nicht erstellt werden.';
    if( payload && payload.label) message = `Es können online maximal ${payload.limit} ${payload.label} angegeben werden.`;
    if( payload && payload.intro) message = payload.intro;
    message += ' Bitte wenden Sie sich an unseren Maklerservice unter '
      + '<span class="font-weight-medium" style="white-space: nowrap">(+49) 02 21 / 37 638 - 92</span> oder an '
      + '<span class="font-weight-medium" style="white-space: nowrap">maklerservice@dmb-rechtsschutz.de</span>. Vielen Dank.';

    commit('setSnackbarError', {
      message,
      timeout: 10000,
      closebutton: true,
      isInfo: true
    });
  },
  /**
   * @param context
   * */
  gewerbeShowKompexHint: ({state, commit}) => {
    let msg = 'Die Zusatzbausteine werden nur in Kombination mit unserem KOMPEX-Rechtsschutz angeboten.';
    msg = state.deckungen.length 
          ? msg + ' Bitte wählen Sie zunächst bei Produktauswahl den KOMPEX.'
          : msg + ' Bitte wählen Sie zunächst die gewünschte Selbstbeteiligung.';
    commit('setSnackbarError', {
      message: msg,
      timeout: 5000,
      closebutton: true,
      isInfo: true
    });
  },
  /**
   * @param context
   * @param payload: object - {
   *    label: string ,
   *    limit: number|string
   *  }
   * */
  gewerbeShowMaxLimit: ({commit}, {label, limit}) => {
    commit('setSnackbarError', {
      message: `Es können online maximal ${limit} ${label} angegeben werden.`,
      timeout: 5000,
      closebutton: true,
      isInfo: true
    });
  },
  gewerbeRecalculateAndValidatePraemien({state, getters, commit}) {
    const previousDeckungen = state.deckungen.map(deckung => ({
      ref: deckung.ref,
      produkt: deckung.produkt,
      immoRef: deckung.immoRef,
      wohnRef: deckung.wohnRef,
      praemie: Object.assign({}, deckung.praemie)
    }));
    const deckungen = state.deckungen.map(deckung => {
      try {
        return Object.assign({}, deckung, {
          praemie: calculateDeckungPraemie(state, getters, deckung)
        });
      } catch (error) {
        throw createPraemienValidationError(deckung);
      }
    });

    commit('gewerbeSetDeckungen', { deckungen });
    commit('gewerbeUpdateTotalPraemie');

    const invalidDeckung = deckungen.find(deckung => getInvalidPraemieKey(state, getters, deckung));
    if (invalidDeckung) {
      throw createPraemienValidationError(invalidDeckung);
    }

    const invalidTotalKey = PRAEMIEN_KEYS.find(key => {
      const value = state.vertrag.praemie[key];
      return typeof value !== 'number' || !Number.isFinite(value) || value <= 0;
    });
    if (invalidTotalKey) {
      const error = new Error('Die Gesamtprämie konnte nicht berechnet werden.');
      error.isPraemienValidationError = true;
      error.deckung = { produkt: 'Gesamtprämie', ref: 'vn1' };
      throw error;
    }

    const changedDeckungen = deckungen.map((deckung, index) => ({ deckung, index }))
      .filter(({deckung, index}) => PRAEMIEN_KEYS.some(key =>
        previousDeckungen[index].praemie[key] !== deckung.praemie[key]));
    return { previousDeckungen, changedDeckungen };
  },
  async gewerbeSubmitFormular(context, { formularType }) {
    const previousTotal = Object.assign({}, context.state.vertrag.praemie);
    const {previousDeckungen, changedDeckungen} = await context.dispatch('gewerbeRecalculateAndValidatePraemien');
    if (changedDeckungen.length) {
      httpService.createInformation({
        event: 'GEWERBE_PRAEMIEN_KORREKTUR',
        formularType,
        tarifVersion: context.state.tarifVersion,
        previousTotal,
        currentTotal: context.state.vertrag.praemie,
        deckungen: changedDeckungen.map(({index}) => ({
          previous: previousDeckungen[index],
          current: context.state.deckungen[index]
        }))
      }).catch(err => console.log(`POST INFORMATION failed with ${err.status}: ${err.message}`));
    }

    const stateToSend = Object.assign({}, context.rootState.gewerbe);
    stateToSend.vertrag = Object.assign({}, context.rootState.gewerbe.vertrag);
    if (stateToSend.ui.isKompex) Object.assign(stateToSend.vertrag, { produktcode: 'G0001'});

    stateToSend.user = context.getters.user;
    stateToSend.user.metadata = Object.assign({}, context.rootState.user.metadata);

    const submitTime = getDateWithTimeString(new Date());

    stateToSend.vertrag.submitTime = submitTime;

    let resultPromise;
    if (formularType === "Antrag") {
      resultPromise = httpService.submitGewerbeAntrag(stateToSend);
    } else {
      resultPromise = httpService.createGewerbeAngebot(stateToSend, formularType);
    }
    return resultPromise.then(formular => {
      const angebotPayload = {
        angebotsnummer: formular.angebotsnummer,
        guid: formular.guid,
        pdfpfad: formular.pdfpfad,
        submitTime
      };
      context.commit("gewerbeSetAngebot", angebotPayload);
      return formular;
    });
  },
  gewerbeResetAngebot(context) {
    context.commit("resetSteps", { page: "gewerbe" });
    context.commit("gewerbeResetAngebotData");
    context.commit("setGewerbeIdentity", context.rootState.user);
    window.location.reload();
  },
  async gewerbeLoadAngebot(context, angebot) {
    await context.dispatch('initializePraemienGewerbeData');

    context.commit("resetSteps", { page: 'gewerbe' });
    context.commit("gewerbeLoadAngebot", angebot);
    const excludeGF =  angebot.deckungen.some(d => d.produkt === 'SPEZ-PRIVMINUSGF') || angebot.ui.excludeGF == true;
    await context.dispatch('gewerbeUpdateMatrixDeckung', context.state.ui.matrixSelection);
    const produkt = 'SPEZ-PRIVMINUSGF';
    if (excludeGF && context.state.ui.matrixSelection.produkte.some(p => /^SPEZ-PBV*/.test(p))) {
      context.dispatch('gewerbeAddZusatzDeckung', { person: 'vn1', sb: context.state.ui.matrixSelection.sb, produkt });
    } 
  },
  gewerbeLoadIdentity(context, userId) {
    return httpService.getWhoAmI(userId).then(user => {
      context.commit("setGewerbeIdentity", user);
      return user;
    })
    .catch(() => {
      // if username / vmnr was invalid, reset identity to current user
      context.commit("setGewerbeIdentity", context.rootState.user);
    });
  }
};

export default {
  state,
  mutations,
  getters,
  actions
};
